// src/pages/PublicCalendarPage.js
import React, { useEffect, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import axios from "axios";
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { API_ENDPOINTS } from '../config/api';
import PageTitle from '../components/PageTitle';

const localizer = momentLocalizer(moment);

// Color mapping for event types
const eventTypeColors = {
  'Clinic': '#aec6e4', // Blue
  'OR': '#ffb3ba',     // Red
  'Meeting': '#b2e0b2', // Green
  'Education': '#ffdfba', // Yellow
  'Journal Club': '#dbffba', // lime 
  'Personal': '#c3b1e1', // Purple
  'Vacation': '#c7eff7', // Blue
  'Sick': '#e9f4b1', // Yellow
  'Weekend Call': '#fab6a3', // Red
  'Other': '#b2e7e0', // Gray
  'default': '#aec6e4'  // Default blue
};

// Custom event component for public view
const CustomEvent = ({ event, view }) => {
  // For month view, show a simpler layout
  if (view === 'month') {
    return (
      <div className="d-flex flex-column gap-1 px-2 py-1" style={{ fontSize: '0.8rem' }}>
        <div className="d-flex align-items-center gap-1">
          <strong><span>{event.label}</span></strong>
          <span className="text-truncate">
            {event.title}
          </span>
        </div>
      </div>
    );
  }

  // For week and day views, show the detailed layout
  return (
    <div className={`d-flex ${event.allDay ? 'flex-row gap-1' : 'flex-column'} gap-1 px-2 py-1`} style={{ fontSize: '0.8rem' }}>
      <div className="d-flex align-items-center gap-1">
        <span>
          <strong>
            {event.label || 'Event'}
          </strong>
        </span>
      </div>
      <span>
        {event.title}
      </span>
      {!event.allDay && (
        <span className="text-secondary mt-1" style={{ fontSize: '0.75rem' }}>
          {moment.utc(event.start).local().format('h:mm A')} - {moment.utc(event.end).local().format('h:mm A')}
        </span>
      )}
    </div>
  );
};

// Custom toolbar for public view
const CustomToolbar = (toolbar) => {
  const goToToday = () => {
    toolbar.onNavigate('TODAY');
  };

  return (
    <div className="rbc-toolbar">
      <div className="rbc-btn-group">
        <button type="button" className="px-2 py-1" onClick={() => toolbar.onNavigate('PREV')}>
          <i className="bi bi-chevron-left"></i>
        </button>
        <button type="button" className="px-2 py-1" onClick={goToToday}>
          Today
        </button>
        <button type="button" className="px-2 py-1" onClick={() => toolbar.onNavigate('NEXT')}>
          <i className="bi bi-chevron-right"></i>
        </button>
      </div>
      <span className="rbc-toolbar-label">{toolbar.label}</span>
      <div className="rbc-btn-group">
        <button type="button" className={toolbar.view === 'month' ? 'rbc-active' : ''} onClick={() => toolbar.onView('month')}>
          Month
        </button>
        <button type="button" className={toolbar.view === 'week' ? 'rbc-active' : ''} onClick={() => toolbar.onView('week')}>
          Week
        </button>
        <button type="button" className={toolbar.view === 'day' ? 'rbc-active' : ''} onClick={() => toolbar.onView('day')}>
          Day
        </button>
      </div>
    </div>
  );
};

export default function PublicCalendarPage() {
  const { userId } = useParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState("month");

  useEffect(() => {
    const fetchPublicCalendar = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch events for the specific user
        const response = await axios.get(`${API_ENDPOINTS.EVENTS}/public/${userId}`);
        
        // Transform events to match the expected format
        const transformedEvents = response.data.map(event => {
          const startDate = moment.utc(event.start).local().toDate();
          const endDate = moment.utc(event.end).local().toDate();
          
          // For all-day events, ensure they run from 12:00 AM on start day to 11:59 PM on end day
          if (event.allDay) {
            const start = moment(startDate).startOf('day').toDate();
            const end = moment(endDate).startOf('day').add(23, 'hours').add(59, 'minutes').toDate();
            return {
              ...event,
              start: start,
              end: end,
              allDay: true
            };
          }
          
          return {
            ...event,
            start: startDate,
            end: endDate,
            allDay: event.allDay || false
          };
        });

        setEvents(transformedEvents);

        // Try to fetch user info if available
        try {
          const userResponse = await axios.get(`${API_ENDPOINTS.USERS}/public/${userId}`);
          setUserInfo(userResponse.data);
        } catch (userError) {
          console.log('Could not fetch user info:', userError.message);
          // User info is optional, so we don't set an error
        }

      } catch (err) {
        console.error("Error fetching public calendar:", err);
        setError(err.response?.data?.error || "Failed to load calendar");
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchPublicCalendar();
    }
  }, [userId]);

  const handleNavigate = (newDate) => {
    setDate(newDate);
  };

  const handleView = (newView) => {
    setView(newView);
  };

  if (loading) {
    return (
      <div>
        <PageTitle title="Loading..." />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <Spinner 
              animation="border" 
              variant="primary" 
              size="lg"
              className="mb-3"
            />
            <div className="text-muted">Loading shared calendar...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageTitle title="Calendar Error" />
        <Container className="mt-4">
          <Alert variant="danger">
            <Alert.Heading>Error Loading Calendar</Alert.Heading>
            <p>{error}</p>
            <p className="mb-0">
              This calendar may be private or no longer available for sharing.
            </p>
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <PageTitle title="Public Calendar" description="View shared calendar events" />
      <div className="bg-white shadow-sm">
        <Container className="py-3">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                {userInfo ? `${userInfo.firstName} ${userInfo.lastName}'s Calendar` : 'Shared Calendar'}
              </h4>
              <p className="text-muted mb-0">
                This is a public view of a shared calendar
              </p>
            </div>
            <div className="text-end">
              <small className="text-muted">
                Last updated: {moment().format('MMM D, YYYY h:mm A')}
              </small>
            </div>
          </div>
        </Container>
      </div>

      <Container fluid className="mt-3">
        <Row>
          <Col>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-0">
                <div style={{ height: 'calc(100vh - 200px)' }}>
                  <Calendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: '100%' }}
                    views={["month", "week", "day"]}
                    view={view}
                    onView={handleView}
                    date={date}
                    onNavigate={handleNavigate}
                    selectable={false}
                    defaultView="month"
                    popup={true}
                    toolbar={true}
                    step={15}
                    timeslots={1}
                    min={moment().startOf('day').add(.01, 'hours').toDate()}
                    max={moment().startOf('day').add(23.99, 'hours').toDate()}
                    eventPropGetter={(event) => {
                      const backgroundColor = eventTypeColors[event.label] || eventTypeColors.default;
                      return {
                        style: {
                          backgroundColor: backgroundColor,
                          borderRadius: '4px',
                          opacity: 0.8,
                          color: 'black',
                          display: 'block',
                          padding: '0',
                          overflow: 'hidden'
                        },
                        className: 'hide-default-time'
                      };
                    }}
                    components={{
                      event: (props) => <CustomEvent {...props} view={view} />,
                      toolbar: CustomToolbar
                    }}
                    dayLayoutAlgorithm="no-overlap"
                    slotGroupPropGetter={() => ({
                      style: {
                        minHeight: '30px'
                      }
                    })}
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <style>
        {`
          .hide-default-time .rbc-event-label {
            display: none;
          }
          
          .rbc-slot-selection {
            background-color: rgba(59, 130, 246, 0.1) !important;
            border-radius: 6px;
            border: 1px dashed #3B82F6 !important;
            padding: 5px;
            margin-top: 1px;
            color: #3B82F6 !important;
          }
          
          .rbc-selected-cell {
            background-color: rgba(59, 130, 246, 0.1) !important;
            margin: 3px 0;
          }
          
          .rbc-time-slot {
            transition: background-color 0.2s ease;
          }
          
          .rbc-time-slot:hover {
            background-color: rgba(59, 130, 246, 0.05);
          }
          
          .rbc-time-content {
            border-radius: 0 0 8px 8px;
          }
          
          .rbc-time-header-content {
            border-radius: 8px 8px 0 0;
          }
          
          .rbc-time-header.rbc-overflowing {
            max-height: 120px !important;
          }
          
          .rbc-time-view .rbc-allday-cell {
            overflow: auto !important;
          }
        `}
      </style>
    </div>
  );
}
